/**
 * 记忆HTTP API服务
 *
 * 封装用户记忆和技能文档相关的REST API调用。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type {
  UserMemoriesResponse,
  WorkspaceMemoriesResponse,
  SkillDocumentsResponse,
  CreateMemoryRequest,
  UpdateMemoryRequest,
  UserMemory,
  WorkspaceMemory,
} from '../types/memory';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class MemoryHttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'MemoryHttpError';
  }
}

export class MemoryUnauthorizedError extends MemoryHttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'MemoryUnauthorizedError';
  }
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // ignore
  }

  switch (response.status) {
    case 401:
      throw new MemoryUnauthorizedError(errorMessage);
    default:
      throw new MemoryHttpError(errorMessage, response.status);
  }
}

export class MemoryApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 获取用户记忆列表
   */
  async getUserMemories(): Promise<UserMemoriesResponse> {
    const response = await fetch(`${this.baseUrl}/api/memory/user`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 删除用户记忆
   */
  async deleteUserMemory(memoryId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/memory/user/${memoryId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  /**
   * 获取用户的技能文档列表
   */
  async getSkillDocuments(): Promise<SkillDocumentsResponse> {
    const response = await fetch(`${this.baseUrl}/api/memory/skills`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 删除技能文档
   */
  async deleteSkillDocument(skillId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/memory/skills/${skillId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  async getWorkspaceMemories(workspaceId: number): Promise<WorkspaceMemoriesResponse> {
    const response = await fetch(`${this.baseUrl}/api/memory/workspace/${workspaceId}`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async createUserMemory(data: CreateMemoryRequest): Promise<UserMemory> {
    const response = await fetch(`${this.baseUrl}/api/memory/user`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateUserMemory(memoryId: number, data: UpdateMemoryRequest): Promise<UserMemory> {
    const response = await fetch(`${this.baseUrl}/api/memory/user/${memoryId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async createWorkspaceMemory(workspaceId: number, data: CreateMemoryRequest): Promise<WorkspaceMemory> {
    const response = await fetch(`${this.baseUrl}/api/memory/workspace/${workspaceId}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateWorkspaceMemory(workspaceId: number, memoryId: number, data: UpdateMemoryRequest): Promise<WorkspaceMemory> {
    const response = await fetch(`${this.baseUrl}/api/memory/workspace/${workspaceId}/${memoryId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async deleteWorkspaceMemory(workspaceId: number, memoryId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/memory/workspace/${workspaceId}/${memoryId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }
}

export const memoryApi = new MemoryApiService();

export default memoryApi;
